import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, CheckCircle2, Crown, BookHeart, NotebookPen, ArrowRight } from "lucide-react";

const benefits = [
  "Daily Ritual check-ins with wellness score & trends",
  "ShapeUp Holistic Weight Loss plan & menu builder",
  "Daily scripture, prayer, gratitude & motivation",
  "Full premium content library",
  "AI wellness assistant & lab explainer",
  "Streaks, milestones & gentle rewards",
];

export default function Membership() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("annual");
  const active = user?.subscription_status === "active" || user?.role === "admin";

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        <div className="text-center animate-fade-up">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">Membership</span>
          <h1 className="mt-2 font-serif text-5xl tracking-tight text-wine">
            {active ? "You're a BloomHer Member" : "Unlock your full ritual"}
          </h1>
        </div>

        {active ? (
          <div className="mt-10 bg-white rounded-[2rem] border border-taupe p-8 sm:p-12">
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-[#EAF0EC] flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-[#4A6B53]" strokeWidth={1.5} />
              </div>
              <h2 className="mt-6 font-serif text-3xl text-[#3A1230]">Your membership is active</h2>
              <p className="mt-2 text-[#6E5862]">You have full access to everything BloomHer offers. Enjoy your daily ritual, love.</p>
              {user?.subscription_started_at && (
                <p className="mt-4 text-sm text-[#9B7E8D]">
                  {user?.subscription_plan === "annual" ? "Annual" : "Monthly"} member since {new Date(user.subscription_started_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="mt-8 border-t border-taupe pt-8">
              <p className="text-xs uppercase tracking-[0.2em] text-[#9B7E8D] text-center mb-6">What's included in your membership</p>
              <ul className="space-y-4 max-w-md mx-auto">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-wine/10 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-wine" strokeWidth={2.5} />
                    </span>
                    <span className="text-base text-[#6E5862]">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="mt-10 bg-white rounded-[2rem] border border-taupe shadow-[0_20px_40px_rgba(58,18,48,0.08)] p-8 sm:p-12">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="h-5 w-5 text-gold" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.2em] text-[#9B7E8D]">BloomHer Member</span>
            </div>

            {/* Plan toggle */}
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="plan-monthly" onClick={() => setPlan("monthly")}
                className={`rounded-2xl border-2 p-4 text-left transition-all ${plan === "monthly" ? "border-wine bg-wine/5" : "border-taupe hover:border-wine/40"}`}>
                <p className="text-xs uppercase tracking-[0.15em] text-[#9B7E8D]">Monthly</p>
                <p className="mt-1 font-serif text-3xl text-wine">$29<span className="text-base text-[#9B7E8D]">/mo</span></p>
              </button>
              <button data-testid="plan-annual" onClick={() => setPlan("annual")}
                className={`relative rounded-2xl border-2 p-4 text-left transition-all ${plan === "annual" ? "border-wine bg-wine/5" : "border-taupe hover:border-wine/40"}`}>
                <span className="absolute -top-2.5 right-3 rounded-full bg-gold px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">Best value</span>
                <p className="text-xs uppercase tracking-[0.15em] text-[#9B7E8D]">Annual</p>
                <p className="mt-1 font-serif text-3xl text-wine">$99<span className="text-base text-[#9B7E8D]">/yr</span></p>
              </button>
            </div>
            <p className="mt-3 text-sm text-[#4A6B53] font-medium">
              {plan === "annual" ? "Save $249 a year vs. monthly 🌷" : "Switch to annual and save $249 a year."}
            </p>

            <ul className="mt-8 space-y-4">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-wine/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-wine" strokeWidth={2.5} />
                  </span>
                  <span className="text-base text-[#6E5862]">{b}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="subscribe-btn"
              onClick={subscribe}
              disabled={busy}
              className="mt-10 flex w-full items-center justify-center gap-2 rounded-full bg-wine px-8 py-4 text-sm font-medium text-white transition-all hover:bg-wine-dark disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : (plan === "annual" ? "Become a Member — $99/yr" : "Become a Member — $29/mo")}
            </button>
            <p className="mt-4 text-center text-xs text-[#9B7E8D]">
              Secure checkout via Stripe. Clinical care is handled by our partner platform.
            </p>
          </div>
        )}

        {/* Transformation Upgrade Plans */}
        <div data-testid="upgrade-plans" className="mt-8 rounded-[2rem] border border-gold/30 bg-[#F7E9EF] p-8 sm:p-10">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-gold" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.2em] text-[#9B7E8D]">Collections</span>
          </div>
          <h2 className="mt-3 font-serif text-4xl tracking-tight text-wine">BloomHer Transformation Upgrade Plans</h2>
          <p className="mt-3 text-base text-[#6E5862] max-w-2xl">
            Ready for deeper, personal support? Join one of our Collections — a <strong className="font-medium text-[#3A1230]">1:1 weekly, personal holistic-practitioner-led</strong> journey. Choose the
            <strong className="font-medium text-[#3A1230]"> Radiance Plan</strong> or the <strong className="font-medium text-[#3A1230]">FullBloom Plan</strong>, each lovingly paired with your BloomHer Book and a Personalized Journal.
          </p>

          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            <div className="rounded-3xl bg-white border border-taupe p-6">
              <h3 className="font-serif text-2xl text-[#3A1230]">Radiance Plan</h3>
              <p className="mt-1 text-sm text-[#6E5862]">Weekly 1:1 holistic practitioner-led sessions to help you glow from within.</p>
              <ul className="mt-4 space-y-2 text-sm text-[#6E5862]">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-wine" /> Weekly 1:1 holistic guidance</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-wine" /> My BloomHer app included</li>
                <li className="flex items-center gap-2"><BookHeart className="h-4 w-4 text-wine" /> BloomHer Book included</li>
                <li className="flex items-center gap-2"><NotebookPen className="h-4 w-4 text-wine" /> Personalized Journal</li>
              </ul>
            </div>
            <div className="rounded-3xl bg-white border border-taupe p-6">
              <h3 className="font-serif text-2xl text-[#3A1230]">FullBloom Plan</h3>
              <p className="mt-1 text-sm text-[#6E5862]">Our most complete transformation — deeper, ongoing personal practitioner partnership.</p>
              <ul className="mt-4 space-y-2 text-sm text-[#6E5862]">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-wine" /> Everything in Radiance, expanded</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-wine" /> My BloomHer app included</li>
                <li className="flex items-center gap-2"><BookHeart className="h-4 w-4 text-wine" /> BloomHer Book included</li>
                <li className="flex items-center gap-2"><NotebookPen className="h-4 w-4 text-wine" /> Personalized Journal</li>
              </ul>
            </div>
          </div>

          <Link to="/coaching" data-testid="upgrade-ask-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-wine px-7 py-3.5 text-sm font-medium text-white hover:bg-wine-dark transition-all">
            Ask about Transformation Upgrade Plans <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </AppLayout>
  );
}
